import type { ContentReplaceModifier } from '@carboncopyco/types';
import { TextBase, TextMenuHeader } from '@carboncopyco/ui';
import { useMemo } from 'react';
import { RulePreview } from '../../RulePreview';
import {
  RuleCardDropdown,
  type RuleCardDropdownProps,
  RulesContent,
} from '../../RulesManager';
import type { DynamicResponseModifier } from './CreateDynamicResponse';
import { DynamicResponseModifierForm } from './DynamicResponseModiferForm';

type DynamicResponseModifierPreviewProps = ContentReplaceModifier['arguments'];

const DynamicResponseModifierPreview = ({
  match,
  replace,
}: DynamicResponseModifierPreviewProps) => {
  return (
    <RulePreview>
      <div className="flex flex-1 gap-1.5 min-w-0">
        <RulePreview.Tooltip title={match}>
          <div className="truncate max-w-[200px]">
            <TextBase
              label={match}
              color="text-ds-natural-600"
              ellipsis
            />
          </div>
        </RulePreview.Tooltip>
        <RulePreview.Arrow />
        <RulePreview.Tooltip title={replace}>
          <div className="truncate max-w-[200px]">
            <TextBase
              label={replace}
              color="text-ds-natural-600"
              ellipsis
            />
          </div>
        </RulePreview.Tooltip>
      </div>
    </RulePreview>
  );
};

type DynamicResponseListItemProps = {
  modifier: DynamicResponseModifier;
  isOpen?: boolean;
  onEdit: (modifier: DynamicResponseModifier) => void;
  onDelete: (modifier: DynamicResponseModifier) => void;
  openRule: () => void;
  onCancel: () => void;
};

export const DynamicResponseModifiersListItem = ({
  isOpen,
  modifier,
  onEdit,
  openRule,
  onDelete,
  onCancel,
}: DynamicResponseListItemProps) => {
  const { match, replace } = modifier.arguments;

  const dropdownItems = useMemo(
    () =>
      [
        {
          type: 'edit',
          onClick: openRule,
        },
        {
          type: 'delete',
          onClick: () => onDelete(modifier),
        },
      ] as RuleCardDropdownProps['items'],
    [modifier, openRule, onDelete]
  );

  return (
    <RulesContent.List.Item
      id={modifier.id}
      header={
        isOpen ? (
          <TextMenuHeader
            label="Condition settings"
            color="text-ds-natural-600"
          />
        ) : (
          <DynamicResponseModifierPreview
            match={match}
            replace={replace}
          />
        )
      }
      content={
        <DynamicResponseModifierForm
          onSubmit={onEdit}
          modifier={modifier}
          onCancel={onCancel}
        />
      }
      action={<RuleCardDropdown items={dropdownItems} />}
    />
  );
};
